import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter, switchMap } from 'rxjs';

import { CatsRecordTypeEnum } from '@shared-enums/cats.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { safeCompare } from '@shared-helpers/string-number-helpers';
import * as sharedApi from '@shared-interfaces/api';
import { IDayOfWeek } from '@shared-interfaces/dates';

import { IVerticalBulkAddPerDiemValue } from '../../components/modal/modal-content-vertical-bulk-add-per-diem/modal-content-vertical-bulk-add-per-diem.component';
import { INumberBoxClickedEvent } from '../../components/table-like-layout/table-like-layout.component';
import { ITableConfigEntity, ITextClickEvent } from '../../interfaces/internal-interfaces';
import { IAnyInterfaceType } from '../../scripts/basicInterface';
import { Employee } from '../../scripts/employee';
import { Equipment } from '../../scripts/equipment';
import { ProductionEquipment } from '../../scripts/productionEquipment';
import { ProductionQuantity } from '../../scripts/ProductionQuantity';
import { ProductionText } from '../../scripts/ProductionText';
import {
  makePayrollEmployeeSummaryConfig,
  makePayrollEquipmentWeeklyConfig,
  makePayrollProductionQuantityWeeklyConfig,
  payrollProductionTextWeeklyConfig,
} from '../../scripts/table-configs/payroll-tables.config';
import { DatahubService, IApplicationQueryParams } from '../../services/datahub.service';
import { MessageService } from '../../services/message.service';
import { ModalService } from '../../services/modal.service';

type ISectionTabs = 'Employees' | 'Equipment' | 'Production';

@Component({
  selector: 'payroll-overview',
  templateUrl: './payroll-overview.component.html',
  standalone: false,
})
export class PayrollOverviewComponent implements OnInit {
  public active: ISectionTabs = 'Employees';
  public readonly timesheet = this.datahub.payrollTimesheet;
  public approvingName = this.datahub.user.approvingName;
  public costCenters: string[] = [];
  public readonly omitActivityCodesFromTotalRow: string[] = [CatsRecordTypeEnum.EmployeePerDiem];
  public readonly sectionTabs: ISectionTabs[] = ['Employees', 'Equipment', 'Production'];

  public employees: Employee[] = [];
  public readonly employeeConfig = makePayrollEmployeeSummaryConfig();

  public equipment: (Equipment | ProductionEquipment)[] = [];
  public readonly equipmentConfig = makePayrollEquipmentWeeklyConfig();

  public production: ProductionQuantity[] = [];
  public readonly productionConfig = makePayrollProductionQuantityWeeklyConfig();

  public productionText: ProductionText[] = [];
  public productionTextConfig = payrollProductionTextWeeklyConfig;

  private selectedCostCenter = '';
  private readonly allCostCentersText = 'All';
  private readonly disabledIconStr = '!cursor-not-allowed !opacity-25';
  private readonly foreignEmployeeTimeEvalError = 'This employee does not report to you.';

  constructor(
    private readonly datahub: DatahubService,
    private readonly modalService: ModalService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.datahub.switchApprover$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (x) => {
        this.switchForeman(x);
      },
      error: (err) => {
        console.error('Error switching approver:', err);
      },
    });
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      //we can't change the typedef on the subscription form angular, so we force them here to be accurate to what they really are
      next: (params) => {
        this.readQueryParams(params as IApplicationQueryParams);
      },
      error: (err) => {
        console.error('Error reading query params:', err);
      },
    });

    // Render the page data when the timesheet says that data is all ready
    this.timesheet.torAndRosterFinished$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((isDoneLoading) => isDoneLoading),
      )
      .subscribe({
        next: () => {
          this.costCenters = this.datahub.user.costCentersForApproval.concat([this.allCostCentersText]);
          this.selectedCostCenter = this.allCostCentersText;
          this.approvingName = this.datahub.user.approvingName;
          this.equipment = [].concat(this.timesheet.equipment).concat(this.timesheet.productionEquipment);
          this.employees = this.timesheet.employees;
          this.production = this.timesheet.productionQuantity;
          this.productionText = this.timesheet.productionText;

          if (this.timesheet.timesheetStatus.number !== TimesheetStatusEnum.InProgress) {
            this.timesheet.disableApproval(
              'payroll',
              `All payroll records approved, ${this.timesheet.timesheetStatus.description}`,
            );
          }

          this.timesheet.viewFinished();

          // Initiate payroll total "time eval" boxes as greyed out and with hover tooltip
          this.getAllEmployeeTimeEvalTableConfigs().forEach((tableConfig) => {
            this.disableTimeEvalCell(tableConfig, this.datahub.payrollTimesheet.timeEvalLoadingMessage);
          });
        },
        error: (err) => {
          console.error('Error in torAndRosterFinished$ subscription:', err);
        },
      });

    //Until time eval loads we need to disable clicks on those cells and make them look disabled
    this.timesheet.timeEvalFinished$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((isDoneLoading) => isDoneLoading),
      )
      .subscribe({
        next: () => {
          this.getAllEmployeeTimeEvalTableConfigs().forEach((tableConfig: ITableConfigEntity, index) => {
            const empId = this.employees[index].EmpId;
            if (this.datahub.user.isDirectReport(empId)) this.enableTimeEvalCell(tableConfig);
            else this.foreignEmployeeTimeEval(tableConfig, this.foreignEmployeeTimeEvalError);
          });
        },
        error: (err) => {
          console.error('Error in timeEvalFinished$ subscription:', err);
        },
      });
  }

  private readQueryParams(params: IApplicationQueryParams): void {
    const approver = params.approver || this.datahub.user.getApprover();
    if (!params.wedate) {
      this.timesheet.reset();
      return;
    }
    this.timesheet.weDate.setWeDateWithString(params.wedate, false);
    if (safeCompare(approver, this.datahub.user.approvingId)) {
      this.timesheet.reset();
    } else {
      this.datahub.user.switchApprover$(approver).subscribe({
        next: () => {
          this.timesheet.reset();
        },
        error: (err) => {
          console.error('Error switching approver:', err);
        },
      });
    }
  }

  changeTab(tab: ISectionTabs): void {
    this.datahub.detailPayneHandler.showSummary();
    this.active = tab;
  }

  handleCostCenterTabChange(tab: string): void {
    this.selectedCostCenter = tab;

    //Disable the buttons in the header on the ALL tab UNLESS there is only one cost center
    const shouldShowHeaderPerDiemButtons =
      this.selectedCostCenter !== this.allCostCentersText ||
      (this.selectedCostCenter === this.allCostCentersText && this.datahub.user.costCentersForApproval.length === 1);
    this.employeeConfig.forEach((x) => {
      if (x.type === 'number-grid') {
        x.shouldDetectHeaderClick = shouldShowHeaderPerDiemButtons;
        x.isDisabled = !shouldShowHeaderPerDiemButtons;
      }
    });

    if (this.selectedCostCenter === this.allCostCentersText) {
      this.timesheet.showAll(this.timesheet.employees);
      this.timesheet.showAll(this.timesheet.equipment);
      this.timesheet.filterArrayItemByCostCenter(this.timesheet.productionEquipment, '');
      this.timesheet.filterArrayItemByCostCenter(this.timesheet.productionQuantity, '');
      this.timesheet.filterArrayItemByCostCenter(this.timesheet.productionText, '');
    } else {
      this.timesheet.filterByCostCenter(this.selectedCostCenter);
    }
    this.datahub.recalcTotals();
    this.datahub.detailPayneHandler.showSummary();
  }

  handleWeDateChanged(): void {
    this.timesheet.navigateToSelf();
  }

  switchForeman(approver: sharedApi.IWebServerResponseApprover): void {
    this.approvingName = approver.display;
    this.costCenters = approver.costCenters;
    this.datahub.payrollTimesheet.navigateToSelf(approver.value);
  }

  approve(): void {
    void this.timesheet.approve(true);
  }

  public addEmployee(): void {
    this.modalService
      .launchResponsiveModal('entity-number-lookup', { entityLookupType: 'employee' }, { autoFocus: 'first-tabbable' })
      .afterClosed()
      .pipe(filter((val) => val != null))
      .subscribe({
        next: (value: sharedApi.IHttpResponseEmployee) => {
          if (this.timesheet.employees.some((x) => x.EmpId === value.EmpId)) {
            //Tried to add an employee already on this timesheet
            this.messageService.info(
              `Employee "<strong>${value.FirstName} ${value.LastName}</strong>" is already included on this timesheet`,
            );
          } else {
            //adding an employee to the timesheet
            const employee = new Employee(value, this.datahub, this.modalService, 'payroll');
            employee.setCatsRecords([]);
            if (!this.datahub.user.isDirectReport(employee.EmpId)) {
              const timeEvalConfig = this.getTimeEvalConfig(employee);
              this.foreignEmployeeTimeEval(timeEvalConfig, this.foreignEmployeeTimeEvalError);
            }
            //add it to the top of the list
            this.timesheet.employees.unshift(employee);
          }
        },
        error: (err) => {
          console.error('Error in addEmployee => afterClosed subscription:', err);
        },
      });
  }

  public addEquipment(): void {
    this.modalService
      .launchResponsiveModal('entity-number-lookup', { entityLookupType: 'equipment' }, { autoFocus: 'first-tabbable' })
      .afterClosed()
      .pipe(filter((val) => val != null))
      .subscribe({
        next: (value: sharedApi.IHttpResponseEquipment) => {
          if (this.timesheet.equipment.some((x) => x.EquipmentId === value.EquipmentId)) {
            //Tried to add equipment already on this timesheet
            this.messageService.info(`Equipment "<strong>${value.Description}</strong>" is already included on this timesheet`);
          } else {
            //adding equipment to the timesheet
            const equipmentItem = new Equipment(value, this.datahub, 'payroll');
            equipmentItem.calculatePayrollGrid();

            //add it to the top of the list
            this.timesheet.equipment.unshift(equipmentItem);
            this.equipment = [].concat(this.timesheet.equipment).concat(this.timesheet.productionEquipment);
          }
        },
        error: (err) => {
          console.error('Error in addEquipment => afterClosed subscription:', err);
        },
      });
  }

  changeDetailPayne(event: INumberBoxClickedEvent<IAnyInterfaceType, IDayOfWeek>): void {
    event.entity.launchPayrollDetailPayneHandler(event.row, event.column);
  }

  public handleHeaderClick(event: ITableConfigEntity): void {
    //We have to re-cast the event type here since the HTML template doesn't yet properly deal with this
    const typedEvent = event as ITableConfigEntity<IDayOfWeek>;
    const employeesCount = this.timesheet.employees.filter(
      (emp) => emp.shouldShow && this.datahub.user.isDirectReport(emp.EmpId),
    ).length;
    const selectedDate = this.datahub.payrollTimesheet.getDate(typedEvent.key);

    let costCenter = this.selectedCostCenter === this.allCostCentersText ? undefined : this.selectedCostCenter;
    if (this.selectedCostCenter === this.allCostCentersText && this.datahub.user.costCentersForApproval.length === 1) {
      //If ALL is selected, but there is only one cost center we will auto-select that one
      costCenter = this.datahub.user.costCentersForApproval[0];
    }

    this.modalService
      .launchResponsiveModal(
        'vertical-bulk-add-per-diem',
        {
          employeesCount,
          formattedDate: selectedDate.display,
          costCenter,
        },
        { autoFocus: 'first-tabbable' },
      )
      .afterClosed()
      .pipe(filter((result: IVerticalBulkAddPerDiemValue[] | undefined) => !!result))
      .subscribe({
        next: (result: IVerticalBulkAddPerDiemValue[]) => {
          this.datahub.payrollTimesheet.bulkAddPerDiemForShownEmployees(selectedDate, result);
        },
        error: (err) => {
          console.error('Error in handleHeaderClick => afterClosed subscription:', err);
        },
      });
  }

  handleTextClick(event: ITextClickEvent<IAnyInterfaceType>): void {
    //will be disabled while loading, so don't allow a click during this time
    if (!event.config.isDisabled) {
      if (event.config.clickValue === 'horizontalBulkEntry' && !(event.entity instanceof ProductionText)) {
        event.entity.showBulkAdd();
      } else if (event.config.clickValue === 'timeEval') {
        this.handleTimeEvalClicked(event);
      } else if (event.config.clickValue === 'horizontalPerDiemBulkEntry') {
        (event.entity as Employee).handlePerDiemTotalClicked(event);
      }
    }
  }

  showTextDetailPayne(event: INumberBoxClickedEvent<ProductionText, IDayOfWeek>): void {
    event.entity.getPayrollDetailPayneValues(event.row, event.column);
    this.datahub.detailPayneHandler.showPayrollProduction(event.entity);
  }

  private disableTimeEvalCell(tableConfig: ITableConfigEntity, tooltipText: string) {
    tableConfig.isDisabled = true;
    tableConfig.icon = this.disabledIconStr;
    tableConfig.tooltip = tooltipText;
  }

  private foreignEmployeeTimeEval(tableConfig: ITableConfigEntity, tooltipText: string) {
    tableConfig.shouldDetectClick = false;
    this.enableTimeEvalCell(tableConfig, tooltipText);
  }

  private handleTimeEvalClicked(event: ITextClickEvent<IAnyInterfaceType>): void {
    const employee = event.entity as Employee;
    this.datahub.payrollTimesheet.setNonbillTimesheet(this.datahub.user.approvingId, this.datahub.payrollTimesheet.extDocuments);
    this.modalService
      .launchResponsiveModal('time-eval', { employee })
      .afterClosed()
      .pipe(
        filter(
          (shouldUseAutomationOverride: boolean | undefined) =>
            shouldUseAutomationOverride != null &&
            employee.payrollSimulator.isPayrollAutomationOverridden !== shouldUseAutomationOverride,
        ),
        switchMap((shouldUseAutomationOverride: boolean) => {
          // This fires when the "Accept" button is pressed
          // Use a temp/loading/saving icon in the mean time, and prevent future clicks until saving has completed
          this.disableTimeEvalCell(event.config, this.datahub.payrollTimesheet.timeEvalSavingMessage);
          return employee.payrollSimulator.togglePayrollOverride$(
            shouldUseAutomationOverride,
            employee,
            this.datahub.payrollTimesheet,
          );
        }),
      )
      .subscribe({
        next: () => {
          this.enableTimeEvalCell(event.config);
        },
        error: (err) => {
          console.error('Error while toggling time eval override', err);
        },
      });
  }

  private enableTimeEvalCell(tableConfig: ITableConfigEntity, tooltipText?: string) {
    tableConfig.isDisabled = false;
    if (tooltipText) tableConfig.tooltip = tooltipText;
    if (tableConfig.icon === this.disabledIconStr) {
      tableConfig.icon = '';
    }

    if (
      tableConfig.tooltip === this.datahub.payrollTimesheet.timeEvalLoadingMessage ||
      tableConfig.tooltip === this.datahub.payrollTimesheet.timeEvalSavingMessage
    ) {
      tableConfig.tooltip = '';
    }
  }

  private getAllEmployeeTimeEvalTableConfigs(): ITableConfigEntity[] {
    return this.employees.map((employee) => this.getTimeEvalConfig(employee));
  }

  private getTimeEvalConfig(employee: Employee): ITableConfigEntity {
    return employee.payrollConfig.find((x) => x.clickValue === 'timeEval');
  }
}
